/**
 * Scenario landing page — mood selection, mobile carousel and per-mood sections.
 *
 * Nothing here hard-codes a product handle or variant ID. Every panel ("complete set"
 * and "what's included") is rendered by Liquid with its own product link, so switching
 * mood or set only toggles which panel is visible — this script never rewrites a URL.
 *
 * A mood may offer more than one set (for example "with keycaps" and "no keycaps").
 * Those live as slides inside one [data-set-group], and the group's left/right switcher
 * shows one slide at a time in every group with the same key, so the "complete set" panel
 * and the "what's included" grid always describe the same set.
 *
 * This page never adds to cart. Price, stock, variants and checkout stay on the real
 * Shopify product each panel links to.
 */

class ScenarioPage extends HTMLElement {
  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('[data-mood-input]'));
    this.setGroups = Array.from(this.querySelectorAll('[data-set-group]'));
    this.primaryCta = this.querySelector('[data-scenario-cta][data-cta-role="primary"]');
    this.status = this.querySelector('[data-mood-status]');
    this.selected = null;

    if (!this.inputs.length) return;

    this.inputs.forEach((input) => {
      input.addEventListener('change', () => this.select(input));
      // Keyboard users tabbing through the carousel should bring the card into view.
      input.addEventListener('focus', () => this.scrollCardIntoView(input));
    });

    this.bindCardClicks();

    this.setupCarousel();
    this.setupSetSwitchers();
    this.setupPrimaryCta();
    this.setupPolicyDialogs();
    this.setupQuickView();
    this.restoreSelection();
    this.applyDefaultMood();
    this.sync();
    if (this.deepLinked) this.applyMoodHero();
  }

  storageKey() {
    return `scenario-mood:${this.dataset.scenarioName || 'default'}`;
  }

  /* ------------------------------------------------------------- carousel */

  /**
   * Handle card clicks ourselves.
   *
   * Letting the browser activate the <label> natively makes it focus the visually
   * hidden radio, and the browser then scrolls that 1x1px element into view — which
   * yanks the whole page upwards whenever the card sits near the top of the viewport.
   * Preventing the default and focusing with `preventScroll` keeps the reader's
   * position exactly where it was.
   */
  bindCardClicks() {
    this.querySelectorAll('.scenario-mood__card').forEach((card) => {
      const input = card.querySelector('[data-mood-input]');
      if (!input) return;

      card.addEventListener('click', (event) => {
        if (input.disabled) return;
        event.preventDefault();
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        try {
          input.focus({ preventScroll: true });
        } catch {
          input.focus();
        }
      });
    });
  }

  setupCarousel() {
    this.trackEl = this.querySelector('[data-mood-track]');
    if (!this.trackEl) return;

    this.viewport = this.querySelector('.scenario-mood__viewport');
    this.cards = Array.from(this.trackEl.querySelectorAll('.scenario-mood__card'));
    this.dots = Array.from(this.querySelectorAll('[data-mood-dot]'));

    this.querySelector('[data-mood-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-mood-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goToCard(index)));

    this.setupCarouselLoop();

    // The swipe hint has done its job as soon as the shopper moves the track - by finger,
    // arrow or dot. Uses a distance threshold rather than a bare scroll event so a stray
    // zero-distance scroll on load cannot dismiss it before anyone has read it.
    this.hintEl = this.querySelector('[data-mood-hint]');
    if (this.hintEl) {
      this.trackEl.addEventListener(
        'scroll',
        () => {
          if (this.trackEl.scrollLeft > 8) this.hideSwipeHint();
        },
        { passive: true },
      );
    }

    let ticking = false;
    this.trackEl.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.syncDots();
          ticking = false;
        });
      },
      { passive: true },
    );

    // 轮播控件只在放不下时出现；窗口尺寸变化要重新测量。
    this.syncOverflow();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncOverflow());
      this.resizeObserver.observe(this.trackEl);
    } else {
      window.addEventListener('resize', () => this.syncOverflow());
    }
  }

  /** 轨道横向溢出（卡片放不下）时才显示箭头与圆点。 */
  /**
   * A plain scroll-snap strip dead-ends at both sides: on the first card there is nothing
   * further left to drag, so the shopper cannot reach the last card without going all the
   * way around. Native scrolling cannot wrap on its own, so detect the out-of-range swipe
   * gesture ourselves and send the strip to the other end. Dragging the finger to the RIGHT
   * reveals whatever sits off-screen to the LEFT, which is what the wrap has to answer.
   */
  setupCarouselLoop() {
    if (!this.trackEl || !this.cards || this.cards.length < 2) return;
    const EDGE = 2; // px - tolerance for "the strip is already hard against this end"
    const SWIPE = 45; // px - deliberate horizontal travel before we call it a wrap
    let startX = null;
    let startScrollLeft = 0;

    this.trackEl.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          startX = null;
          return;
        }
        startX = event.touches[0].clientX;
        startScrollLeft = this.trackEl.scrollLeft;
      },
      { passive: true },
    );

    this.trackEl.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const max = this.trackEl.scrollWidth - this.trackEl.clientWidth;
        startX = null;
        if (Math.abs(dx) < SWIPE) return;
        if (dx > 0 && startScrollLeft <= EDGE) {
          this.wrapTo(this.cards.length - 1);
        } else if (dx < 0 && startScrollLeft >= max - EDGE) {
          this.wrapTo(0);
        }
      },
      { passive: true },
    );
  }

  /** Move to a card without the strip stopping at the end. */
  wrapTo(index) {
    const card = this.cards[index];
    if (!card || !this.trackEl) return;
    const track = this.trackEl;
    const target = card.offsetLeft;
    const before = track.scrollLeft;
    if (Math.abs(before - target) < 4) return; // already showing it
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
    // Safety net: Chrome drops the very first smooth scroll on a scroller that has never
    // been scrolled, which would make the shopper's first wrap gesture do nothing at all
    // (measured: call 1 dead, calls 2+ fine). If nothing moved, jump instead of stalling.
    window.setTimeout(() => {
      if (Math.abs(track.scrollLeft - before) < 4) {
        track.scrollTo({ left: target, behavior: 'auto' });
      }
    }, 450);
  }

  /** Hide the mobile swipe hint once the shopper has moved the track themselves. */
  hideSwipeHint() {
    if (this.hintEl && !this.hintEl.hidden) this.hintEl.hidden = true;
  }

  syncOverflow() {
    if (!this.trackEl || !this.viewport) return;
    const overflowing = this.trackEl.scrollWidth > this.trackEl.clientWidth + 2;
    this.viewport.dataset.overflow = overflowing ? 'true' : 'false';
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /** Distance between two card starts, including the gap. */
  stepSize() {
    if (!this.cards || this.cards.length < 2) return this.trackEl ? this.trackEl.clientWidth : 0;
    return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
  }

  currentIndex() {
    const size = this.stepSize();
    if (!size) return 0;
    return Math.round(this.trackEl.scrollLeft / size);
  }

  step(direction) {
    const total = this.cards ? this.cards.length : 0;
    if (!total) return;
    // Wrap rather than clamp, so the arrows loop the strip the same way the swipe does.
    const index = (((this.currentIndex() + direction) % total) + total) % total;
    this.goToCard(index);
  }

  goToCard(index) {
    const card = this.cards && this.cards[index];
    if (card) this.scrollCardIntoView(card);
  }

  scrollCardIntoView(element) {
    if (!this.trackEl) return;
    const card = element.closest ? element.closest('.scenario-mood__card') : null;
    if (!card) return;
    // On desktop the track is a plain grid, so there is nothing to scroll.
    if (this.trackEl.scrollWidth <= this.trackEl.clientWidth + 1) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.trackEl.scrollTo({ left: card.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  syncDots() {
    if (!this.dots || !this.dots.length) return;
    const index = this.currentIndex();
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  /* ---------------------------------------------------------- primary CTA */

  /**
   * "Continue to the set" stays on the page: it scrolls down to the details for the
   * chosen mood. The purchase button lives inside each panel with its own product URL.
   */
  setupPrimaryCta() {
    if (!this.primaryCta) return;

    const targetId = this.primaryCta.dataset.scrollTarget;
    this.scrollTarget = targetId ? this.querySelector('#' + CSS.escape(targetId)) : null;

    this.primaryCta.addEventListener('click', (event) => {
      if (this.primaryCta.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        this.announcePrompt();
        const first = this.inputs.find((input) => !input.disabled);
        if (first) first.focus();
        return;
      }
      if (!this.scrollTarget) return;

      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollTarget.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

      this.dispatchEvent(new CustomEvent('scenario:cta_clicked', {
        bubbles: true,
        detail: {
          scenario_name: this.dataset.scenarioName || '',
          mood_name: this.selected ? this.selected.value : '',
        },
      }));
    });
  }

  announcePrompt() {
    if (!this.status) return;
    this.status.textContent = this.dataset.chooseMoodLabel || 'Choose a mood to continue';
    this.status.classList.add('is-error');
  }

  /* ------------------------------------------------------------ selection */

  restoreSelection() {
    // CHANGE 2: ?mood=<slug> deep link wins over any stored choice.
    // An invalid or unknown slug is ignored silently and the page falls back.
    let requested = null;
    try {
      requested = new URLSearchParams(window.location.search).get('mood');
    } catch {
      requested = null;
    }
    if (requested) {
      const slug = this.moodSlug(requested);
      const match = this.inputs.find((input) => !input.disabled && this.moodSlug(input.value) === slug);
      if (match) {
        match.checked = true;
        this.selected = match;
        this.deepLinked = true;
        return;
      }
    }

    let saved = null;
    try {
      saved = window.sessionStorage.getItem(this.storageKey());
    } catch {
      return; // private mode / storage disabled
    }
    if (!saved) return;

    const match = this.inputs.find((input) => input.value === saved && !input.disabled);
    if (!match) return;
    match.checked = true;
    this.selected = match;
  }

  /**
   * A first-time visitor has not chosen anything, and without a selection the page falls
   * back to the block-level "default" panel - which is a single hard-coded set and does not
   * follow the card order. That made the page show one mood's set under a different mood's
   * first card. Default to the FIRST mood in the list instead, so the bundle panel, the
   * "what's included" grid, the sticky bar and the highlighted card all describe the same set.
   *
   * This is a silent default, not a choice: it does not fire the mood_selected analytics event
   * and does not write to sessionStorage, so a real selection still wins on later visits.
   */
  applyDefaultMood() {
    if (this.selected) return;
    const first = this.inputs.find((input) => !input.disabled);
    if (!first) return;
    first.checked = true;
    this.selected = first;
  }

  /** "Cozy Fall Season" -> "cozy-fall-season" */
  moodSlug(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * CHANGE 2: a ?mood= landing may use that mood's hero variant. This runs once on load
   * and never during browsing, so the hero stays fixed while the visitor looks around.
   */
  applyMoodHero() {
    if (!this.selected) return;
    // The hero slot the section emits is 1-based (`hero_mood_1..3` in the Theme Editor),
    // while data-panel-key is the 0-based mood index. Without this +1 the lookup always
    // missed and the ?mood= hero variant never swapped in.
    const heroSlot = Number(this.selected.dataset.panelKey) + 1;
    const variant = this.querySelector('[data-hero-mood="' + heroSlot + '"]');
    if (!variant) return; // no variant configured for this mood - keep the default hero

    const defaults = this.querySelectorAll('[data-hero-default], [data-hero-default-mobile]');
    defaults.forEach((el) => { el.hidden = true; });
    variant.hidden = false;
  }

  select(input) {
    // Selecting a mood must never move the reader. Some browsers scroll a focused
    // visually-hidden control into view, and swapping panels can shift layout, so
    // record the position and put it back if anything moved it.
    const lockedY = window.scrollY;

    this.selected = input;
    try {
      window.sessionStorage.setItem(this.storageKey(), input.value);
    } catch {
      /* storage unavailable — selection still works for this page view */
    }
    this.sync();
    this.announce(input);
    this.trackMood(input);
    this.restoreScroll(lockedY);
  }

  /** Put the viewport back where it was if the selection shifted it. */
  restoreScroll(lockedY) {
    const compare = () => Math.abs(window.scrollY - lockedY) > 1;
    if (compare()) window.scrollTo({ top: lockedY, behavior: 'auto' });
    // Layout from the panel swap can settle a frame later, so check once more.
    window.requestAnimationFrame(() => {
      if (compare()) window.scrollTo({ top: lockedY, behavior: 'auto' });
    });
  }

  announce(input) {
    if (!this.status) return;
    this.status.classList.remove('is-error');
    this.status.textContent = `${input.value} selected.`;
  }

  /**
   * One set switcher per mood. Groups with a second set get arrows and dots; groups in the
   * same mood are driven together, so the "complete set" panel and the "what's included"
   * grid can never disagree about which set the shopper is looking at.
   */
  setupSetSwitchers() {
    this.setGroups.forEach((group) => {
      const slides = Array.from(group.querySelectorAll('[data-set-index]'));
      if (slides.length < 2) {
        group.dataset.activeSet = '0';
        return;
      }

      const bar = group.querySelector('.scenario-sets__bar');
      // The switcher label must be looked up by its OWN attribute: the slides also carry
      // data-set-label (their set name), and group.querySelector would match a slide first
      // in a group that has no bar - writing textContent into it wiped the whole
      // "what's included" grid.
      const label = group.querySelector('[data-set-current-label]');
      const dots = Array.from(group.querySelectorAll('[data-set-dot]'));
      if (bar) bar.hidden = false;

      const go = (index) => this.setActiveSet(group, index);
      group.__goToSet = go;

      group.querySelector('[data-set-prev]')?.addEventListener('click', () => {
        go(Number(group.dataset.activeSet || 0) - 1);
      });
      group.querySelector('[data-set-next]')?.addEventListener('click', () => {
        go(Number(group.dataset.activeSet || 0) + 1);
      });
      dots.forEach((dot, index) => dot.addEventListener('click', () => go(index)));

      // Swipe support. A horizontal drag past the threshold switches set; a mostly
      // vertical drag is left alone so normal page scrolling still works.
      let startX = null;
      let startY = null;
      group.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
        },
        { passive: true },
      );
      group.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          startX = null;
          startY = null;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          go(Number(group.dataset.activeSet || 0) + (dx < 0 ? 1 : -1));
        },
        { passive: true },
      );

      if (label) label.textContent = slides[0].dataset.setLabel || '';
      this.setActiveSet(group, 0);
    });
  }

  /** Show one set inside a group, and keep every group of the same mood in step. */
  setActiveSet(group, index) {
    const key = group.dataset.setGroup;
    const siblings = this.setGroups.filter((g) => g.dataset.setGroup === key);
    const slides = Array.from(group.querySelectorAll('[data-set-index]'));
    const total = slides.length;
    if (!total) return;
    const next = Math.min(Math.max(index, 0), total - 1);

    siblings.forEach((sibling) => {
      const siblingSlides = Array.from(sibling.querySelectorAll('[data-set-index]'));
      if (siblingSlides.length !== total) return;
      sibling.dataset.activeSet = String(next);
      siblingSlides.forEach((slide, i) => {
        slide.hidden = i !== next;
      });
      const siblingLabel = sibling.querySelector('[data-set-current-label]');
      if (siblingLabel) siblingLabel.textContent = siblingSlides[next].dataset.setLabel || '';
      sibling.querySelectorAll('[data-set-dot]').forEach((dot, i) => {
        const active = i === next;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    });

    this.syncSticky();
  }

  /** The slide the shopper is currently looking at for the chosen mood, if any. */
  activeSetSlide() {
    if (!this.selected) return null;
    const key = this.selected.dataset.panelKey;
    // Only the set groups carry prices; the mood preview figure also uses data-set-group
    // for visibility and would otherwise shadow the real panel in the query.
    const group = this.querySelector(`.scenario-sets[data-set-group="${key}"]`);
    if (!group) return null;
    const index = Number(group.dataset.activeSet || 0);
    return group.querySelector(`[data-set-index="${index}"]`);
  }

  /** Show the panel group matching the chosen mood ('default' when nothing is chosen). */
  sync() {
    const key = this.selected ? this.selected.dataset.panelKey : 'default';

    this.setGroups.forEach((group) => {
      group.hidden = group.dataset.setGroup !== key;
    });

    if (this.primaryCta) {
      if (this.selected) {
        this.primaryCta.removeAttribute('aria-disabled');
      } else {
        this.primaryCta.setAttribute('aria-disabled', 'true');
      }
    }

    if (this.status) {
      this.status.classList.remove('is-error');
      this.status.textContent = this.selected
        ? `${this.selected.value} selected — you can continue.`
        : this.dataset.chooseMoodLabel || 'Choose a mood to continue';
    }

    this.syncDots();
    this.syncSticky();
  }

  /* --------------------------------------------------------- quick view */

  /**
   * CHANGE 4: "View details" opens a Quick View dialog in place. A native <dialog>
   * opened with showModal() already traps focus, closes on ESC and restores focus to
   * the trigger, so we only need to add backdrop-click closing.
   */
  setupQuickView() {
    this.querySelectorAll('[data-quick-view]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const dialog = this.querySelector('#' + CSS.escape(trigger.dataset.quickView));
        // If the dialog is unavailable, let <a href> fall through to the real page.
        if (!dialog || typeof dialog.showModal !== 'function') return;
        event.preventDefault();
        dialog.showModal();
      });
    });

    this.querySelectorAll('dialog.scenario-quickview').forEach((dialog) => {
      dialog.querySelectorAll('[data-quick-view-close]').forEach((btn) => {
        btn.addEventListener('click', () => dialog.close());
      });
      // A click landing on the dialog itself (not its inner panel) is a backdrop click.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  /**
   * Shipping / Returns policy links open in the same dialog as the component
   * "View details", so the shopper never loses the scenario page. The real policy
   * pages still exist: without JS (or if the dialog is missing) the link navigates
   * as usual, and it is rendered with target="_blank" so the page is never lost.
   *
   * Any link pointing at those pages is wired up - including links the merchant
   * typed into FAQ answers - by matching the URL against the two configured pages.
   */
  setupPolicyDialogs() {
    const targets = [
      ['shipping', this.dataset.policyShippingUrl],
      ['returns', this.dataset.policyReturnsUrl],
    ].filter(([, url]) => url);
    if (!targets.length) return;

    const links = Array.from(this.querySelectorAll('a[href]'));

    targets.forEach(([kind, url]) => {
      const dialog = this.querySelector('[data-policy-dialog="' + kind + '"]');
      if (!dialog) return;

      const path = (() => {
        try {
          return new URL(url, window.location.origin).pathname;
        } catch {
          return url;
        }
      })();

      links.forEach((link) => {
        if (link.dataset.quickView) return; // already wired in Liquid
        const href = link.getAttribute('href') || '';
        if (!href.includes('/pages/')) return;
        if (href === url || href === path || href.startsWith(path)) {
          link.dataset.quickView = dialog.id;
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener');
        }
      });
    });
  }

  /* ------------------------------------------------------ sticky summary */

  /** Mobile-only bar: chosen mood, item count, live price and the purchase CTA. */
  syncSticky() {
    const bar = this.querySelector('[data-sticky-summary]');
    if (!bar) return;

    const moodLabel = bar.querySelector('[data-sticky-mood]');
    const meta = bar.querySelector('[data-sticky-meta]');
    const cta = bar.querySelector('[data-cta-role="sticky"]');

    if (!this.selected) {
      bar.hidden = true;
      return;
    }

    bar.hidden = false;
    if (moodLabel) moodLabel.textContent = this.selected.value;
    // The sticky bar follows the set the shopper is looking at, not just the mood: a mood
    // can sell two sets at different prices, so the price and the buy link must move with it.
    const slide = this.activeSetSlide();
    const slidePrice = slide ? slide.dataset.setPrice : '';
    const slideItems = slide ? slide.dataset.setItems : '';
    const slideUrl = slide ? slide.dataset.setUrl : '';
    if (meta) {
      meta.textContent = [
        slideItems || this.selected.dataset.moodItems,
        slidePrice || this.selected.dataset.moodPrice,
        this.shippingNote(slide),
      ]
        .filter(Boolean)
        .join(' · ');
    }
    if (cta) {
      cta.setAttribute(
        'href',
        slideUrl || this.selected.dataset.moodUrl || ('#' + (this.dataset.moodGroup || '')),
      );
      cta.removeAttribute('aria-disabled');
    }
  }

  /** Free-shipping line for the sticky bar, based on the configured threshold. */
  shippingNote(slide) {
    const threshold = Number(this.dataset.freeShippingThreshold) || 0;
    if (!threshold) return '';
    const activePrice = slide && slide.dataset.setPriceCents;
    const price = Number(activePrice || this.selected.dataset.moodPriceCents) || 0;
    if (price >= threshold) return this.dataset.freeShippingLabel || '';
    const shortfall = ((threshold - price) / 100).toFixed(2).replace(/\.00$/, '');
    return `${this.dataset.freeShippingProgressLabel || 'Free standard shipping over'} $${shortfall} more`;
  }

  /** Behavioural analytics only — no shopper data, no Shopify standard event replacement. */
  trackMood(input) {
    this.dispatchEvent(new CustomEvent('scenario:mood_selected', {
      bubbles: true,
      detail: {
        scenario_name: this.dataset.scenarioName || '',
        mood_name: input.value,
      },
    }));
  }
}

if (!customElements.get('scenario-page')) {
  customElements.define('scenario-page', ScenarioPage);
}
